import mixpanel from 'mixpanel-browser';

import { LoginMethod, PhoneUpdateStatus } from './enum';
import { AffiliateRes } from './types';

mixpanel.init(process.env.REACT_APP_MIXPANEL_TOKEN || '', {
  autocapture: true,
  track_pageview: false,
  debug: process.env.NODE_ENV === 'development',
  api_host: process.env.REACT_APP_MIXPANEL_API_HOST || 'https://api.mixpanel.com',
  persistence: 'localStorage'
})

const getDeviceType = (): 'mobile' | 'desktop' => {
  const userAgent = navigator.userAgent.toLowerCase();
  const isMobile = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(userAgent);
  return isMobile ? 'mobile' : 'desktop';
};

const getBaseProperties = (affiliateId: string, affiliate?: AffiliateRes) => ({
  affiliate_id: affiliateId,
  country: affiliate?.country || '',
  device_type: getDeviceType()
});

export const trackAffiliatePortalViewed = (affiliateId: string, affiliate?: AffiliateRes) => {
  mixpanel.track('affiliate_portal_viewed', getBaseProperties(affiliateId, affiliate));
};

export const trackCodeCopied = (affiliateId: string, codeName: string, affiliate?: AffiliateRes) => {
  mixpanel.track('code_copied', {
    ...getBaseProperties(affiliateId, affiliate),
    code_name: codeName
  });
};

export const trackContactEmailCopied = (affiliateId: string, email: string, affiliate?: AffiliateRes) => {
  mixpanel.track('contact_owner_email_copied', {
    ...getBaseProperties(affiliateId, affiliate),
    contact_email: email
  });
};

export const trackAffiliateShareSheetOpened = (affiliateId: string, codeName: string, affiliate?: AffiliateRes) => {
  mixpanel.track('affiliate_share_sheet_opened', {
    ...getBaseProperties(affiliateId, affiliate),
    code_name: codeName
  });
};

export const trackNewReferralToast = (affiliateId: string, transactionCount: number, totalAmount?: string, affiliate?: AffiliateRes) => {
  mixpanel.track('new_referral_toast', {
    ...getBaseProperties(affiliateId, affiliate),
    transaction_count: transactionCount,
    total_amount: totalAmount
  });
};

export const getUserProperties = (affiliate: AffiliateRes) => ({
  $name: affiliate?.name,
  $email: affiliate?.email,
  $phone: affiliate?.phoneNumber,
  country: affiliate?.country,
  currency: affiliate?.currency,
  date_joined: affiliate?.dateJoined,
  total_users_attracted: affiliate?.totalUsersAttracted,
  code_name: affiliate?.codeName
});

export const identifyUser = (affiliateId: string, userProperties?: Record<string, any>) => {
  mixpanel.identify(affiliateId);
  if (userProperties) {
    mixpanel.people.set(userProperties);
  }
};

export const trackEvent = (eventName: string, affiliateId: string, properties?: Record<string, any>, affiliate?: AffiliateRes) => {
  mixpanel.track(eventName, {
    ...getBaseProperties(affiliateId, affiliate),
    ...properties
  });
};

export const trackSignInAttempt = (phoneNumber: string, deviceType?: 'mobile' | 'desktop', loginMethod?: LoginMethod) => {
  mixpanel.track('sign_in_attempt', {
    phone_number: phoneNumber,
    device_type: deviceType || getDeviceType(),
    affiliate_login_method: loginMethod || LoginMethod.PHONE
  });
};

export const trackSignInOTPSent = (phoneNumber: string, deviceType?: 'mobile' | 'desktop', loginMethod?: LoginMethod) => {
  mixpanel.track('sign_in_otp_sent', {
    phone_number: phoneNumber,
    device_type: deviceType || getDeviceType(),
    affiliate_login_method: loginMethod || LoginMethod.PHONE
  });
};

export const trackSignInSuccess = (phoneNumber: string, deviceType?: 'mobile' | 'desktop', loginMethod?: LoginMethod) => {
  mixpanel.track('sign_in_success', {
    phone_number: phoneNumber,
    device_type: deviceType || getDeviceType(),
    affiliate_login_method: loginMethod || LoginMethod.PHONE
  });
};

export const trackSignInError = (phoneNumber: string, errorType: string, errorMessage: string, deviceType?: 'mobile' | 'desktop', loginMethod?: LoginMethod) => {
  mixpanel.track('sign_in_error', {
    phone_number: phoneNumber,
    error_type: errorType,
    error_message: errorMessage,
    device_type: deviceType || getDeviceType(),
    affiliate_login_method: loginMethod || LoginMethod.PHONE
  });
};

export const trackOTPResend = (phoneNumber: string, deviceType?: 'mobile' | 'desktop') => {
  mixpanel.track('otp_resend', {
    phone_number: phoneNumber,
    device_type: deviceType || getDeviceType()
  });
};

export const trackPhoneNumberEdit = (phoneNumber: string, deviceType?: 'mobile' | 'desktop') => {
  mixpanel.track('phone_number_edit', {
    phone_number: phoneNumber,
    device_type: deviceType || getDeviceType()
  });
};

export const trackPhoneAddedPostLogin = (affiliateId: string, phoneNumber: string, status: PhoneUpdateStatus, deviceType?: 'mobile' | 'desktop') => {
  mixpanel.track('phone_added_post_login', {
    affiliate_id: affiliateId,
    phone_number: phoneNumber,
    status: status,
    device_type: deviceType || getDeviceType()
  });
};

export default mixpanel; 